/**
 * What: Shopify shopper intelligence merchant-facing copy.
 * Transport / data-flow: Pure string dictionary segment merged by shopify-component-copy.ts for Shopify UI consumers.
 *
 */
export const shopifyComponentStoreIntelligenceCopy = {
    storeIntelligenceStoreIntelligencePanel: {
        syncDispatchUncertain051: "The sync request is still being confirmed. This page will refresh when the build is ready.",
        backfillMode014: "Backfill mode:",
    },
    storeIntelligenceProductSignalBreakdownCard: {
        productsToInvestigate001: "Products And Patterns",
        oneProductWithStoreActivity002: "1 product or pattern with store activity",
        productsWithStoreActivity003: "products and patterns with store activity",
        shopifyProductInterests004: "Shopify product interests",
        product005: "Product or pattern",
        purchaseSignal006: "Purchase activity",
        browsingSignal007: "Browsing activity",
        suggestedFocus008: "Suggested focus",
        noPurchaseSignal009: "No purchase activity",
        oneCustomer010: "1 customer",
        customers011: "customers",
        noBrowsingSignal012: "No browsing activity",
        oneVisitor013: "1 visitor",
        visitors014: "visitors",
        strongInterestAcrossBrowsingAndPurchase015: "Strong all-around interest",
        browsedMoreThanBought016: "Browsed more than bought",
        knownCustomerInterest017: "Known customer interest",
        reviewBeforeTesting018: "Worth reviewing",
        noProductsToInvestigate019: "No product activity is available yet.",
        tip020: "Review these before choosing what to test next.",
        shopifyPurchaseSignals021: "Shopify purchase signals",
        browsingSignals022: "Browsing signals"
    },
    storeIntelligenceStoreIntelligenceRelationshipMap: {
        overlap001: "Overlap",
        overlap004: "overlap",
        noVisibleOverlapsYet005: "No visible overlaps yet",
        relationshipFormulasAreUnavailableUntil006: "Relationship formulas are unavailable until enough shopper-background signals clear the privacy threshold.",
        relationshipOverlaps007: "Relationship overlaps",
        pairwiseFormulasShowWhichShopper008: "Pairwise formulas show which shopper-background signals co-occur. Treat them as context, not recommendations or causal evidence."
    },
    storeIntelligenceSduiWidgetBlock: { relationshipPatternsFallbackTitle001: "Relationship Patterns" },
    storeIntelligenceProductBuyerPersonaCard: {
        heading: "Who Buys This Product",
        customerBreakdownPrefix: "Customer breakdown from",
        verifiedStoreOrdersSuffix: "verified store orders",
        topCustomerGroupPrefix: "Top Customer Group:",
        customerLoyaltyMixHeading: "Customer Loyalty Mix",
        buyersSuffix: "buyers",
        noSegmentedData: "No segmented customer data recorded for this product.",
        discountAndPricingHabitsHeading: "Discount & Pricing Habits",
        discountedOrdersLabel: "Discounted Orders",
        pricingBehaviorLabel: "Pricing Behavior",
        frequentlyBuysWithDiscounts: "Frequently Buys with Discounts",
        fullPriceDemand: "Full-Price Demand",
        whatCustomersCareAboutLabel: "What customers care about:"
    },
    storeIntelligenceProductReviewIntelligenceCard: {
        heading: "Customer Review Insights",
        whatShoppersSayPrefix: "What shoppers say in",
        verifiedCustomerReviewsSuffix: "verified customer reviews",
        highConfidenceTier: "High Confidence",
        directionalSignalTier: "Directional Read",
        sparseDataTier: "Sparse Data",
        proofGapBannerTitlePrefix: "Review Proof Gap:",
        proofGapBannerTitleSuffix: "claim(s) lack customer proof",
        proofGapBannerBody: "Some marketing claims in your product story aren't mentioned in customer reviews. Test customer-validated angles in Test Builder to see what actually convinces shoppers to buy.",
        customerSatisfactionHeading: "Customer Satisfaction & Sentiment",
        averageRatingLabel: "Average Rating",
        ratingOutOf: "/ 5.0",
        customerSentimentLabel: "Customer Sentiment",
        positiveSuffix: "Positive",
        happyCustomersLabel: "Happy Customers",
        satisfiedSuffix: "Satisfied",
        issuesReportedSuffix: "Issues Reported",
        mostMentionedTopicsHeading: "Most Mentioned Topics",
        noThemesIdentified: "No review theme clusters identified yet.",
        storeClaimsVsReviewProofHeading: "Store Claims vs. Review Proof",
        confirmedByReviewsBadge: "Confirmed by reviews",
        customerConcernsReportedBadge: "Customer concerns reported",
        notMentionedInReviewsBadge: "Not mentioned in reviews"
    },
    storeIntelligenceProductCrossSellAuditCard: {
        heading: "Frequent Add-ons & Bundle Opportunities",
        whatCustomersBuyTogetherPrefix: "Frequently bought together with this item (",
        productOrdersSuffix: "product orders)",
        topAddOnPrefix: "Top Add-on: +",
        topAddOnLiftSuffix: "x Cart Frequency",
        activeBundlesHeading: "Your Store's Active Bundles",
        strongPerformer: "Strong Performer",
        lowAdoption: "Low Adoption",
        noDeclaredBundles: "No declared bundle or frequently-bought-together metafields found.",
        frequentlyBoughtTogetherHeading: "Frequently Bought Together",
        boughtTogetherInPrefix: "Bought together in",
        ordersAboveStoreAverageSuffix: "of orders (",
        liftAboveStoreAverageSuffix: "x more likely in the same cart)",
        moreFrequentBadgePrefix: "+",
        moreFrequentBadgeSuffix: "x More Frequent",
        noOrganicCompanions: "No organic companion pairings with high basket lift detected."
    },
    storeIntelligenceProductCategoryOutlierCard: {
        heading: "Store Category Comparison",
        howThisComparesPrefix: "How this product compares to typical items in",
        itemsInPrefix: "items in \"",
        itemsInSuffix: "\"",
        typicalForStore: "Typical for store",
        aboveAverage: "Higher than category median",
        belowAverage: "Lower than category median",
        currentPrefix: "Current:",
        typicalInCategoryPrefix: "Typical in category:"
    },
    storeIntelligenceProductCustomerIntelligenceCard: {
        heading: "Customer Intelligence",
        subtitle: "Observed purchasing patterns from recent store orders.",
        statusObserved: "Store Data",
        statusLimited: "Limited store data",
        buyerLifecycleHeading: "Buyer Lifecycle",
        repeatBehaviorHeading: "Repeat Behavior",
        overlapHeading: "Other Products Bought by These Customers",
        overlapSubtitle: "Other catalog items purchased by customers who bought this product.",
        overlapEmpty: "No other products were purchased by these customers in recent store orders.",
        unavailableTitle: "Customer intelligence is not available yet",
        unavailableExplanation: "No customer pattern has been calculated for this product yet."
    },
    storeIntelligenceProductOpportunityEvidenceSection: {
        noEvidenceTitle: "No customer evidence available for this product",
        noEvidenceBody: "No customer evidence could be calculated for this product from the available store data.",
        partialEvidenceTitle: "Some customer evidence is not available",
        partialEvidenceSuffix: "have not been calculated.",
        buyerBehaviorLabel: "buyer behavior",
        reviewsLabel: "reviews",
        crossSellLabel: "cross-sell",
        categoryComparisonLabel: "category comparison",
        customerIntelligenceLabel: "customer intelligence",
        observedRecordSingular: "observed record",
        observedRecordPlural: "observed records",
        capturedPrefix: "captured"
    },
};
//# sourceMappingURL=store-intelligence.js.map